/* single-variant-enforcer.js

RULE:
- Only ONE variant (size) per product can be in the cart.
- If a product is already in cart, any attempt to add another size is reverted:
  remove the newly-added line item(s) and keep the existing one.
- Show message.
- Keep cart bubble correct.
- IMPORTANT: Keep cart drawer functional (trash button etc.) by re-rendering the cart drawer
  via Shopify section rendering (NO manual DOM deletion of line items).

Works with Horizon cart drawer + app-injected quick add.
*/

(function () {
  const ORIGINAL_FETCH = window.fetch.bind(window);

  let running = false;
  let lastSnapshot = new Map(); // line key -> qty
  let lastToastAt = 0;
  let lastCartSig = "";

  /* ---------------- UI: toast ---------------- */

  function toast(message) {
    const now = Date.now();
    if (now - lastToastAt < 800) return;
    lastToastAt = now;

    let el = document.querySelector("[data-one-variant-toast]");
    if (!el) {
      el = document.createElement("div");
      el.setAttribute("data-one-variant-toast", "true");
      el.style.position = "fixed";
      el.style.top = "16px";
      el.style.left = "50%";
      el.style.transform = "translateX(-50%)";
      el.style.zIndex = "99999";
      el.style.maxWidth = "min(720px, calc(100% - 24px))";
      el.style.padding = "12px 14px";
      el.style.borderRadius = "12px";
      el.style.boxShadow = "0 6px 20px rgba(0,0,0,.18)";
      el.style.background = "white";
      el.style.color = "black";
      el.style.fontSize = "14px";
      el.style.display = "flex";
      el.style.gap = "10px";
      el.style.alignItems = "center";
      el.style.border = "1px solid rgba(0,0,0,.12)";
      document.body.appendChild(el);
    }

    el.innerHTML = `<span aria-hidden="true">⛔</span><span>${message}</span>`;
    el.style.opacity = "1";
    clearTimeout(el._t);
    el._t = setTimeout(() => (el.style.opacity = "0"), 2500);
  }

  /* ---------------- Cart API ---------------- */

  async function getCart() {
    const res = await ORIGINAL_FETCH("/cart.js", { credentials: "same-origin" });
    return res.json();
  }

  async function updateCartByKey(updatesByKey) {
    const res = await ORIGINAL_FETCH("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ updates: updatesByKey }),
    });
    return res.json();
  }

  function snapshotCart(cart) {
    const snap = new Map();
    for (const item of cart.items || []) snap.set(item.key, item.quantity);
    return snap;
  }

  function detectNewLines(prevSnap, cart) {
    const added = [];
    for (const item of cart.items || []) {
      if (!prevSnap.has(item.key)) added.push(item);
    }
    return added;
  }

  function cartSignature(cart) {
    // Helps avoid doing heavy work when nothing changed
    return (cart.items || [])
      .map((i) => `${i.key}:${i.quantity}`)
      .join("|");
  }

  /* ---------------- Bubble update (robust) ---------------- */

  function updateCartBubbleCount(count) {
    const c = String(count);

    const selectors = [
      "[data-cart-count]",
      "[data-cart-count-bubble]",
      ".cart-count-bubble",
      ".cart-count",
      "#cart-icon-bubble",
      "#CartCount",
      "[class*='cart'][class*='count']",
      "[id*='cart'][id*='count']",
    ];

    const nodes = new Set();

    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((n) => nodes.add(n));
    });

    // Numeric nodes inside any /cart link
    document.querySelectorAll("a[href*='/cart']").forEach((link) => {
      link.querySelectorAll("*").forEach((el) => {
        const t = (el.textContent || "").trim();
        if (/^[0-9]+$/.test(t) && t.length <= 3) nodes.add(el);
      });
    });

    nodes.forEach((node) => {
      try {
        if (node.hasAttribute("data-cart-count")) node.setAttribute("data-cart-count", c);
        if (node.hasAttribute("data-cart-count-bubble")) node.setAttribute("data-cart-count-bubble", c);

        if (node.childElementCount === 0) node.textContent = c;
        else {
          const span = node.querySelector("span");
          if (span) span.textContent = c;
        }

        node.style.display = Number(count) > 0 ? "" : "none";
      } catch {}
    });
  }

  /* ---------------- Drawer refresh (section re-render) ----------------
     This keeps Horizon’s trash button and drawer interactions working.
  */

  function getRootUrl() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === "string" ? root : "/";
  }

  async function rerenderCartDrawerFromSectionId() {
    const drawer = document.querySelector("cart-drawer-component");
    if (!drawer) return;

    // Horizon shows cart items within this component in your screenshot
    const cartItemsComponent = drawer.querySelector("cart-items-component[data-section-id]");
    if (!cartItemsComponent) return;

    const sectionId = cartItemsComponent.getAttribute("data-section-id");
    if (!sectionId) return;

    const root = getRootUrl();

    // Shopify section rendering by section_id (returns HTML)
    const url = `${root}?section_id=${encodeURIComponent(sectionId)}`;

    const res = await ORIGINAL_FETCH(url, { credentials: "same-origin" });
    if (!res.ok) return;

    const html = await res.text();

    // Parse the returned HTML and extract the new cart-items-component for that section
    const temp = document.createElement("div");
    temp.innerHTML = html;

    // We try a few ways to find the new cart items content
    const newCartItems =
      temp.querySelector(`cart-items-component[data-section-id="${CSS.escape(sectionId)}"]`) ||
      temp.querySelector("cart-items-component[data-section-id]") ||
      temp.querySelector("cart-items-component");

    if (!newCartItems) return;

    // Replace the existing cart-items-component with the new one
    cartItemsComponent.replaceWith(newCartItems);
  }

  async function refreshDrawerAndBubble() {
    // Bubble first
    try {
      const cart = await getCart();
      updateCartBubbleCount(cart.item_count);
    } catch {}

    // Then re-render drawer contents (keeps trash functionality)
    try {
      await rerenderCartDrawerFromSectionId();
    } catch {}

    // Bubble again (in case drawer/header updated count)
    try {
      const cart2 = await getCart();
      updateCartBubbleCount(cart2.item_count);
    } catch {}
  }

  /* ---------------- Enforcement ---------------- */

  async function enforceKeepExistingVariant() {
    if (running) return;
    running = true;

    try {
      const cart = await getCart();
      const sig = cartSignature(cart);

      // Avoid hammering if nothing changed
      if (sig === lastCartSig) {
        running = false;
        return;
      }
      lastCartSig = sig;

      // First run: establish snapshot + bubble
      if (lastSnapshot.size === 0) {
        lastSnapshot = snapshotCart(cart);
        updateCartBubbleCount(cart.item_count);
        running = false;
        return;
      }

      const newlyAddedLines = detectNewLines(lastSnapshot, cart);

      // Group items by product_id
      const byProduct = new Map();
      for (const item of cart.items || []) {
        const pid = Number(item.product_id);
        if (!byProduct.has(pid)) byProduct.set(pid, []);
        byProduct.get(pid).push(item);
      }

      const updates = {};
      let blockedAttempt = false;

      for (const [pid, group] of byProduct.entries()) {
        if (group.length <= 1) continue;

        // Remove newly added line(s) so original stays
        const newOnes = group.filter((g) =>
          newlyAddedLines.some((n) => n.key === g.key)
        );

        if (newOnes.length > 0) {
          for (const n of newOnes) {
            updates[n.key] = 0;
            blockedAttempt = true;
          }
        } else {
          // Fallback: remove the last one in the group
          const newest = group[group.length - 1];
          if (newest && newest.key) {
            updates[newest.key] = 0;
            blockedAttempt = true;
          }
        }

        // Enforce max qty 1 on remaining lines of that product
        for (const item of group) {
          if (!item.key) continue;
          if (updates[item.key] === 0) continue;
          if (item.quantity > 1) updates[item.key] = 1;
        }
      }

      if (Object.keys(updates).length > 0) {
        await updateCartByKey(updates);

        if (blockedAttempt) {
          toast("The maximum quantity of this item is already in your cart.");
        }

        // Re-sync snapshot & UI
        const cartAfter = await getCart();
        lastSnapshot = snapshotCart(cartAfter);
        lastCartSig = cartSignature(cartAfter);

        await refreshDrawerAndBubble();

        // One more refresh shortly after to catch quick-add UI timing
        setTimeout(() => {
          refreshDrawerAndBubble().catch(() => {});
        }, 300);
      } else {
        // No duplicates, keep snapshot current
        lastSnapshot = snapshotCart(cart);
        updateCartBubbleCount(cart.item_count);
      }
    } catch (e) {
      console.error("[single-variant-enforcer] enforce failed:", e);
    } finally {
      running = false;
    }
  }

  /* ---------------- Startup / polling ---------------- */

  window.addEventListener("load", async () => {
    try {
      const cart = await getCart();
      lastSnapshot = snapshotCart(cart);
      lastCartSig = cartSignature(cart);
      updateCartBubbleCount(cart.item_count);
    } catch {}
    setTimeout(enforceKeepExistingVariant, 450);
  });

  // Poll to catch app-driven quick adds
  setInterval(enforceKeepExistingVariant, 700);

  // Keep bubble accurate
  setInterval(() => {
    getCart()
      .then((c) => updateCartBubbleCount(c.item_count))
      .catch(() => {});
  }, 1200);
})();
